/**
 * Sticky Add to Cart Bar
 * ------------------------------------------------------------------------
 * Vanilla JS only — no jQuery / external libraries.
 *
 * What this does:
 *  1. Shows a fixed bottom bar once the visitor scrolls past the product
 *     page's real "Add to cart" button, and hides it again if they scroll
 *     back up to it.
 *  2. Mirrors price + availability from the real product form, so it is
 *     always in sync with whatever variant is currently selected — no
 *     matter which variant picker style (buttons or dropdowns) the theme
 *     is using.
 *  3. Forwards clicks to the REAL "Add to cart" button instead of
 *     re-implementing cart logic. This guarantees the sticky bar always
 *     stays compatible with the existing cart drawer / AJAX cart, Buy Now
 *     / dynamic checkout buttons, and any cart-related apps — because it
 *     simply triggers the exact same code path the existing button does.
 *
 * This file never touches, replaces, or re-submits the original product
 * form. It only reads from it and clicks its existing submit button.
 */
(function () {
  'use strict';

  /**
   * Initialise every sticky bar instance found on the page.
   * (In practice there is one per product section, but the loop keeps
   * this safe if a theme ever renders more than one product form.)
   */
  function init() {
    var bars = document.querySelectorAll('[data-sticky-atc]');
    bars.forEach(setupStickyBar);
  }

  function setupStickyBar(bar) {
    // Guard against double-initialisation. This matters because some
    // theme features (quick view, AJAX section re-renders) can fetch and
    // re-inject markup/scripts elsewhere on the page; without this guard
    // the same bar could end up with two sets of listeners/observers,
    // which is what caused the bar to behave inconsistently.
    if (bar.getAttribute('data-sticky-atc-initialized') === 'true') return;
    bar.setAttribute('data-sticky-atc-initialized', 'true');

    var sectionId = bar.getAttribute('data-section-id');
    var submitId = bar.getAttribute('data-submit-id');
    var targetId = bar.getAttribute('data-target-id');

    var originalSection = targetId ? document.getElementById(targetId) : null;
    if (!originalSection) return;

    // The real "Add to cart" submit button already rendered on the page.
    var originalButton = document.getElementById(submitId) ||
      originalSection.querySelector('[data-type="add-to-cart-form"] [type="submit"]');

    // If we can't find a real button to mirror/forward to, do nothing —
    // better to show no sticky bar than to risk a broken duplicate.
    if (!originalButton) return;

    // The price + availability source elements already kept up to date
    // by the theme's existing variant-change logic.
    var priceSource = originalSection.querySelector('#ProductPrice');
    var priceSourceWrapper = priceSource ? priceSource.closest('[id^="price-"]') : null;

    var priceEl = bar.querySelector('[data-sticky-atc-price]');
    var availabilityEl = bar.querySelector('[data-sticky-atc-availability]');
    var buttonEl = bar.querySelector('[data-sticky-atc-button]');
    var buttonTextEl = bar.querySelector('[data-sticky-atc-button-text]');

    var addToCartLabel = bar.getAttribute('data-label-add') || 'Add to cart';
    var soldOutLabel = bar.getAttribute('data-label-soldout') || 'Sold out';
    var inStockLabel = bar.getAttribute('data-label-instock') || 'In stock';
    var outOfStockLabel = bar.getAttribute('data-label-outofstock') || 'Out of stock';
    var selectVariantLabel = bar.getAttribute('data-label-select-variant') || 'Please select an option';

    // Used to tell "no variant fully selected yet" apart from a genuine
    // sold-out variant. The theme itself swaps the real button's text to
    // this exact "Unavailable" string whenever the chosen combination of
    // options doesn't match any variant — see assets/plugins.js stInaccessible().
    var unavailableText = (window.stThemeString && window.stThemeString.Strunavailable) || null;
    var cartTitleEl = originalButton.querySelector('.cart-title') || originalButton;

    // Used so a click while no variant is selected can scroll the visitor
    // back up to the picker and reuse the theme's own inline error UI.
    var cartActionEl = originalSection.querySelector('cart-action');
    var variantPickerEl = originalSection.querySelector('pr-option, selects-box');

    var isVisible = false;

    /* ----------------------------------------------------------------
     * 1. Keep price + availability + button state in sync.
     *    Three possible states, read from the real button:
     *      - "available"     -> normal, clickable, shows price + in stock
     *      - "no-selection"  -> visitor hasn't fully chosen a variant yet
     *                            (e.g. ring size). Kept CLICKABLE so tapping
     *                            it guides them back to pick one, instead
     *                            of just sitting there greyed out.
     *      - "sold-out"      -> the resolved variant is genuinely out of
     *                            stock. Disabled, same as the real button.
     * ---------------------------------------------------------------- */
    function getState() {
      var isDisabled = originalButton.disabled || originalButton.getAttribute('aria-disabled') === 'true';
      if (!isDisabled) return 'available';
      if (unavailableText && cartTitleEl.textContent.trim() === unavailableText) return 'no-selection';
      return 'sold-out';
    }

    function syncFromOriginal() {
      // Price: copy the exact text already formatted by the theme/Shopify,
      // so currency formatting always matches the rest of the page.
      if (priceEl && priceSource) {
        var priceText = priceSource.textContent.trim();
        if (priceText) priceEl.textContent = priceText;
      }

      var state = getState();
      if (buttonEl) buttonEl.setAttribute('data-state', state);

      if (availabilityEl) {
        if (state === 'no-selection') {
          // Real availability isn't known until a full variant is chosen —
          // stay neutral instead of guessing "in stock" or "sold out".
          availabilityEl.textContent = '';
          availabilityEl.classList.remove('is-in-stock', 'is-sold-out');
        } else {
          var isAvailable = state === 'available';
          availabilityEl.textContent = isAvailable ? inStockLabel : outOfStockLabel;
          availabilityEl.classList.toggle('is-in-stock', isAvailable);
          availabilityEl.classList.toggle('is-sold-out', !isAvailable);
        }
      }

      if (buttonEl) {
        // Only a genuine sold-out variant disables the sticky button.
        // "no-selection" stays clickable on purpose (see click handler).
        var shouldDisable = state === 'sold-out';
        buttonEl.disabled = shouldDisable;
        buttonEl.classList.toggle('is-disabled', shouldDisable);
      }

      if (buttonTextEl) {
        buttonTextEl.textContent = state === 'sold-out' ? soldOutLabel : addToCartLabel;
      }
    }

    /* ----------------------------------------------------------------
     * 2. Mirror the real button's "loading" state while it submits,
     *    so the sticky button shows a matching spinner and can't be
     *    double-clicked mid-request.
     * ---------------------------------------------------------------- */
    function syncLoadingState() {
      var isLoading = originalButton.classList.contains('loading');
      if (buttonEl) buttonEl.classList.toggle('is-loading', isLoading);
    }

    // Observe the real button for disabled / loading-class changes.
    if (window.MutationObserver) {
      var buttonObserver = new MutationObserver(function () {
        syncFromOriginal();
        syncLoadingState();
      });
      buttonObserver.observe(originalButton, {
        attributes: true,
        attributeFilter: ['disabled', 'aria-disabled', 'class']
      });

      // Observe the price element so variant changes (button swatches,
      // dropdown selects, or any app) are reflected instantly.
      if (priceSourceWrapper) {
        var priceObserver = new MutationObserver(syncFromOriginal);
        priceObserver.observe(priceSourceWrapper, { childList: true, subtree: true, characterData: true });
      }
    }

    // Some themes/apps swap the variant id via a plain "change" event on
    // the hidden id input — listen too, as a lightweight extra safety net.
    var hiddenIdInput = originalSection.querySelector('input[name="id"]');
    if (hiddenIdInput) {
      hiddenIdInput.addEventListener('change', function () {
        // Give the theme's own price/availability DOM update a brief
        // moment to complete (it may itself be triggered by this same
        // event) before we copy the result.
        window.setTimeout(syncFromOriginal, 50);
      });
    }

    /* ----------------------------------------------------------------
     * 3. Handle clicks based on the current state:
     *      - "available"    -> forward the click to the REAL Add to cart
     *                           button. We do NOT duplicate cart/AJAX
     *                           logic here, so this stays compatible with
     *                           the cart drawer, AJAX cart, Buy Now
     *                           buttons, and any installed apps.
     *      - "no-selection" -> scroll the visitor up to the variant
     *                           picker and show the theme's own inline
     *                           "please select an option" error.
     *      - "sold-out"     -> genuinely unavailable, do nothing.
     * ---------------------------------------------------------------- */
    function promptVariantSelection() {
      var scrollTarget = variantPickerEl || cartActionEl || originalButton;
      if (scrollTarget && typeof scrollTarget.scrollIntoView === 'function') {
        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      if (cartActionEl && typeof cartActionEl.stHandErrorTxt === 'function') {
        cartActionEl.stHandErrorTxt(selectVariantLabel);
      }
    }

    if (buttonEl) {
      buttonEl.addEventListener('click', function (event) {
        event.preventDefault();
        var state = buttonEl.getAttribute('data-state');
        if (state === 'sold-out') return;
        if (state === 'no-selection') {
          promptVariantSelection();
          return;
        }
        originalButton.click();
      });
    }

    /* ----------------------------------------------------------------
     * 4. Show the bar only once the visitor scrolls past the real
     *    Add to cart button, hide it again if they scroll back up to it.
     * ---------------------------------------------------------------- */
    function setVisible(visible) {
      if (visible === isVisible) return;
      isVisible = visible;
      bar.classList.toggle('is-visible', visible);
      bar.setAttribute('aria-hidden', visible ? 'false' : 'true');
    }

    if (window.IntersectionObserver) {
      var visibilityObserver = new IntersectionObserver(function (entries) {
        var entry = entries[0];
        if (entry.isIntersecting) {
          // The real button is on screen — no need for the sticky bar.
          setVisible(false);
        } else if (entry.boundingClientRect.top < 0) {
          // Scrolled past it (it's above the viewport) — show the bar.
          setVisible(true);
        } else {
          // Hasn't been reached yet (it's below the viewport) — stay hidden.
          setVisible(false);
        }
      }, { threshold: 0 });

      visibilityObserver.observe(originalButton);
    } else {
      // Extremely old browsers without IntersectionObserver: fall back
      // to a lightweight, debounced scroll check.
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          var rect = originalButton.getBoundingClientRect();
          setVisible(rect.bottom < 0);
          ticking = false;
        });
      }, { passive: true });
    }

    // Initial sync on load, in case the server-rendered state in the
    // sticky bar ever needs correcting (e.g. cached HTML).
    syncFromOriginal();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
